import { forwardRef, type ClipboardEvent, type FocusEvent, type KeyboardEvent } from 'react';
import * as stylex from '@stylexjs/stylex';
import { InputPrimitive } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';

/** Text-like input types FPL's input primitive accepts. */
type EmbedInputType = 'text' | 'email' | 'search' | 'tel' | 'url' | 'password';

export interface EmbedInputProps {
  /**
   * Accessible name for the field. Required because `EmbedInput` renders no visible
   * label and no chrome of its own — the surrounding container owns the label/border.
   */
  'aria-label': string;
  value?: string;
  placeholder?: string;
  type?: EmbedInputType;
  name?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'search' | 'tel' | 'url' | 'numeric' | 'decimal' | 'none';
  maxLength?: number;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  /** Sit on the chip wrap row instead of taking the full row. */
  layout?: 'fill' | 'inline';
}

const styles = stylex.create({
  // Chromeless field: it fills its container and inherits the chrome (border,
  // background, focus ring) from a parent such as a chips/pills box. Mirrors what
  // the legacy MUI input did via `sx={{ '& .MuiInputBase-root': { border: 'none' } }}`.
  input: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 0,
    width: '100%',
    minHeight: '2rem',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    padding: 0,
    margin: 0,
    color: token.text,
    cursor: 'text',
    textOverflow: 'ellipsis',
    '::placeholder': {
      color: token.textTertiary,
    },
    '::selection': {
      backgroundColor: token.texthighlight,
    },
  },
  inline: {
    width: 'auto',
    flexBasis: '8rem',
    minWidth: '8rem',
    // Override `input` minHeight 32px so 8px paper padding still fits the 32px Invite row.
    minHeight: '16px',
  },
});

/**
 * Bare FPL text field meant to be embedded inside a container that owns the visual
 * chrome (border, background, focus ring) — e.g. a chips/pills input or a custom
 * paper. Built on FPL's `InputPrimitive` so it carries none of the standalone
 * `Input` chrome that would render a box-in-a-box inside such containers.
 *
 * Presentational and value-first: state and handlers are owned by the caller.
 */
export const EmbedInput = forwardRef<HTMLInputElement, EmbedInputProps>(function EmbedInput(
  {
    'aria-label': ariaLabel,
    value,
    placeholder,
    type = 'text',
    name,
    autoComplete,
    inputMode,
    maxLength,
    disabled,
    readOnly,
    onChange,
    onKeyDown,
    onPaste,
    onFocus,
    onBlur,
    layout = 'fill',
  },
  ref,
) {
  return (
    <InputPrimitive
      ref={ref}
      {...stylex.props(typographyStyles.textBodyMedium, styles.input, layout === 'inline' && styles.inline)}
      aria-label={ariaLabel}
      value={value}
      placeholder={placeholder}
      type={type}
      name={name}
      autoComplete={autoComplete}
      inputMode={inputMode}
      maxLength={maxLength}
      disabled={disabled}
      readOnly={readOnly}
      onChange={onChange ? (next) => onChange(next) : undefined}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
});
